import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Spin,
  Typography,
} from 'antd';

import {
  useEdit1CIntegrationModal,
  useEdit1CIntegrationModalEffects,
} from '@pages/Integrations/IntegrationsList/components/Edit1CIntegrationModal/hooks';
import type { EditMode } from '@pages/Integrations/IntegrationsList/components/Edit1CIntegrationModal/types';

import type { FC } from 'react';

import '@pages/Integrations/IntegrationsList/components/Edit1CIntegrationModal/styles/Edit1CIntegrationModal.desktop.scss';

export interface Edit1CIntegrationModalProps {
  isOpen: boolean;
  integrationId: number | null;
  initialMode?: EditMode;
  onCancel(): void;
  afterSave?(): void;
}

const { Title, Text, Paragraph } = Typography;
const { Item } = Form;
const { Password } = Input;

export const Edit1CIntegrationModal: FC<Edit1CIntegrationModalProps> = ({
  isOpen,
  integrationId,
  initialMode = 'edit',
  onCancel,
  afterSave,
}) => {
  const {
    form,

    mode,
    integrationName,

    warehousesList,
    selectedWarehouseIds,

    isLoadingIntegration,
    isPrimaryActionLoading,
    isPrimaryActionDisabled,
    isSaveValidationVisible,

    handleCancel,
    handleReset,

    handleModeChange,
    handleFetchIntegration,

    handleToggleWarehouse,
    handlePrimaryAction,
  } = useEdit1CIntegrationModal({
    isOpen,
    integrationId,
    initialMode,
    onCancel,
    afterSave,
  });

  useEdit1CIntegrationModalEffects({
    isOpen,
    integrationId,
    initialMode,
    handleReset,
    handleFetchIntegration,
    handleModeChange,
  });

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnHidden
      centered
      className="Edit1CIntegrationModal"
      title={
        <div className="Edit1CIntegrationModal__title">
          <Title level={5} className="Edit1CIntegrationModal__titleText">
            Редактирование интеграции &quot;{integrationName}&quot;
          </Title>
        </div>
      }
    >
      <div className="Edit1CIntegrationModal__content">
        <div className="Edit1CIntegrationModal__mode">
          <Segmented
            value={mode}
            onChange={(value) => handleModeChange(value as EditMode)}
            options={[
              { label: 'Редактировать', value: 'edit' },
              { label: 'Настройки', value: 'options' },
            ]}
          />
        </div>
        <Spin spinning={isLoadingIntegration}>
          {mode === 'edit' ? (
            <div className="Edit1CIntegrationModal__stepContent">
              <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                className="Edit1CIntegrationModal__form"
              >
                <Item
                  label="Адрес сервиса"
                  name="serviceUrl"
                  rules={[
                    { required: true, message: 'Введите адрес сервиса' },
                    {
                      validator: async (_, value?: string) => {
                        if (!value) return;
                        try {
                          new URL(value);
                        } catch {
                          return Promise.reject(new Error('Некорректный URL'));
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder="https://example-1c.company.ru"
                    autoComplete="off"
                    inputMode="url"
                  />
                </Item>

                <Item
                  label="Пользователь"
                  name="login"
                  rules={[{ required: true, message: 'Введите пользователя' }]}
                >
                  <Input
                    placeholder="Введите пользователя"
                    autoComplete="off"
                  />
                </Item>

                <Item
                  label="Пароль"
                  name="password"
                  rules={[{ required: true, message: 'Введите пароль' }]}
                >
                  <Password placeholder="Введите пароль" autoComplete="off" />
                </Item>

                <Text className="Edit1CIntegrationModal__hint" type="secondary">
                  Список складов настраивается в разделе «Настройки».
                </Text>
              </Form>
            </div>
          ) : (
            <div className="Edit1CIntegrationModal__stepContent">
              <Text className="Edit1CIntegrationModal__sectionTitle">
                Выберите склады
              </Text>
              <Paragraph className="Edit1CIntegrationModal__sectionHint">
                Отметьте склады, синхронизируемые в фоновом режиме.
              </Paragraph>

              <div className="Edit1CIntegrationModal__warehouses">
                <Space
                  direction="vertical"
                  size={8}
                  className="Edit1CIntegrationModal__warehousesList"
                >
                  {warehousesList.map((warehouse) => (
                    <Checkbox
                      key={warehouse.id}
                      checked={selectedWarehouseIds.includes(warehouse.id)}
                      onChange={(event) =>
                        handleToggleWarehouse(
                          warehouse.id,
                          event.target.checked
                        )
                      }
                    >
                      {warehouse.name}
                    </Checkbox>
                  ))}
                </Space>
              </div>

              {isSaveValidationVisible && (
                <Text
                  className="Edit1CIntegrationModal__saveValidation"
                  type="danger"
                >
                  Выберите хотя бы один склад.
                </Text>
              )}
            </div>
          )}
        </Spin>
      </div>

      <div className="Edit1CIntegrationModal__footer">
        <Button onClick={handleCancel} disabled={isPrimaryActionLoading}>
          Отменить
        </Button>

        <Button
          type="primary"
          onClick={handlePrimaryAction}
          loading={isPrimaryActionLoading}
          disabled={isPrimaryActionDisabled}
        >
          Сохранить
        </Button>
      </div>
    </Modal>
  );
};
